import { Button, CircularProgress, Icon, icons, Typography, theme } from "cdh-data-portal-theme"
import type { PublicDeviceCodeFlowState } from "../../../hooks/authorization/useAuthorization"
import { formatCountdown, localText } from "./DeviceFlowLogin.static"
import { useStyles } from "./DeviceFlowLogin.style"

interface Props {
  deviceFlow: PublicDeviceCodeFlowState
}

/**
 * Shown once the user has an authenticated session but hasn't completed the AWS SSO device
 * authorization flow yet (see hooks/authorization/useAuthorization.ts / useDeviceCodeFlow.ts).
 * Mirrors cdh-data-portal-internal's SSOLoginDialog, minus the dialog chrome -- ops-hub requires
 * this flow to get any role at all, so it's shown full-page instead of as an opt-in dialog.
 */
export const DeviceFlowLogin: React.FC<Props> = ({ deviceFlow }) => {
  const { classes } = useStyles()

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Icon
          iconPath={{ path: icons.portalLogo.path, viewBox: "0 0 64 64" }}
          color={theme.colors.primary}
          size={40}
        />
        <Typography variant="headline2" className={classes.title}>{localText.title}</Typography>
        {renderContent()}
      </div>
    </div>
  )

  function renderContent() {
    switch (deviceFlow.status) {
      case 'error':
        return (
          <>
            <Typography variant="body" className={classes.subtitle} color={theme.textColors.error}>
              {localText.errorSubtitle}
            </Typography>
            {deviceFlow.error && <Typography color={theme.textColors.error}>{deviceFlow.error}</Typography>}
            <Button variant="primary" title={localText.retryButton} onClick={deviceFlow.startFlow} width={260} />
          </>
        )
      case 'idle':
      case 'starting':
        return (
          <>
            <Typography variant="body" className={classes.subtitle} color={theme.textColors.primaryLighter}>
              {localText.startingSubtitle}
            </Typography>
            <CircularProgress size={24} color={theme.colors.primary} />
          </>
        )
      case 'polling':
        return (
          <>
            <Typography variant="body" className={classes.subtitle} color={theme.textColors.primaryLighter}>
              {localText.subtitle}
            </Typography>
            {deviceFlow.userCode && (
              <div className={classes.codeContainer}>
                <span className={classes.code}>{deviceFlow.userCode}</span>
              </div>
            )}
            {deviceFlow.verificationUri && (
              <Button
                variant="primary"
                title={localText.verifyButton}
                url={deviceFlow.verificationUri}
                openLinkInNewTab
                width={260}
              />
            )}
            {deviceFlow.secondsRemaining !== null && (
              <Typography className={classes.countdown}>
                {formatCountdown(deviceFlow.secondsRemaining)}
              </Typography>
            )}
          </>
        )
      case 'success':
        return null
      default:
        return null
    }
  }
}

export default DeviceFlowLogin
